import * as React from 'react';
import { Dialog, DialogFooter, Label, Link } from 'office-ui-fabric-react';
import { AppButton, getAppButtonStyles, getSecondaryButtonOverrideStyles } from '../AppButton';
import { getTopLabelStyles, getModalProps, getDialogContentProps, getDialogStyles } from '../../common/styles/Popup.styles';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import dispatcher from '../../dispatcher';
import { ChangeEvent, TranscriptionEvent } from '../../stores/utils';
import { EventEmitter } from 'events';
import { EMPTY_STRING } from '../../../AppConstants';

export enum TranscriptionAlertButtonType {
  PRIMARY_BUTTON = 1,
  SECONDARY_BUTTON = 2,
  LINK = 4,
  NONE = 8
};

export interface TranscriptionAlertState {
  showTranscriptionAlert?: boolean,
  information: string,
  title: string,
  buttonInfo: TranscriptionAlertButtonState,
};

export interface TranscriptionAlertButtonState {
  buttonType: TranscriptionAlertButtonType,
  primaryButtonText?: string,
  secondaryButtonText?: string,
  buttonClickListener?: TranscriptionAlertButtonClickListener,
  hyperLink?: string,
  linkText?: string
}

export interface TranscriptionAlertButtonClickListener {
  onButtonClicked(buttonType: TranscriptionAlertButtonType): void;
}

export const TranscriptionAlertActions = {
  ShowTranscriptionAlert: 'SHOW_TRANSCRIPTION_ALERT',
  RemoveTranscriptionAlert: 'REMOVE_TRANSCRIPTION_ALERT'
};

/* TranscriptionAlert is a generic Dialog, which can be used to display information text
and title at runtime, the caller need to pass in all requirement in form of {TranscriptionAlertState},
{TranscriptionAlertButtonState}.
Also caller can register callback for Dialog <buttons></buttons>

sample code {
const listener: TranscriptionAlertButtonClickListener = <TranscriptionAlertButtonClickListener>({
onButtonClicked(buttonType: TranscriptionAlertButtonType): void {}
});

const TranscriptionAlertState: TranscriptionAlertState = {
    information: "",
    title: "",
    buttonInfo: { buttonType: TranscriptionAlertButtonType.POSITIVE_BUTTON, buttonClickListener: listener }
}

dispatcher.dispatch({
    type: TranscriptionAlertActions.ShowTranscriptionAlert,
    payload: TranscriptionAlertState
});
}*/

export class TranscriptionAlert extends React.PureComponent<{}, TranscriptionAlertState> {

  constructor(props: {}) {
    super(props);
    this.state = transcriptionAlertManager.getState();
  }

  public componentDidMount() {
    transcriptionAlertManager.on(ChangeEvent.Change, this._handleUpdate);
  }

  public componentWillUnmount() {
    transcriptionAlertManager.off(ChangeEvent.Change, this._handleUpdate);
  }

  public render() {
    if (!this.state.showTranscriptionAlert) {
      return null;
    }
    const confirmNewTranscriptDialogTitle: string | JSX.Element =
      <Label styles={getTopLabelStyles()}>{this.state.title}</Label>

    return (
      <Dialog
        dialogContentProps={getDialogContentProps(confirmNewTranscriptDialogTitle)}
        modalProps={getModalProps()}
        hidden={!this.state.showTranscriptionAlert}
        styles={getDialogStyles('10px')}>
        {this.state.information.length > 0 ?
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{this.state.information}</Label> : null}
        {this.showButton()}
      </Dialog>
    );
  }

  private _handleUpdate = () => {
    this.setState(transcriptionAlertManager.getState());
  }

  private showButton = () => {
    if (this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.NONE) {
      return null;
    }
    return (<DialogFooter>
      {this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.LINK ?
        <Link
          id="TranscriptionLink"
          styles={AppLayoutSettings.AppCommonStyles.getLinkStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().linkColor)}
          href={this.state.buttonInfo.hyperLink} target="_blank">
          {this.state.buttonInfo.linkText}
        </Link> : null}
      {this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.PRIMARY_BUTTON ?
        <AppButton
          type={'AppButtonProps'}
          id="TranscriptionPrimaryButton"
          styles={getAppButtonStyles()}
          text={this.state.buttonInfo.primaryButtonText == undefined ? TranscriptionStrings.OKLabel : this.state.buttonInfo.primaryButtonText}
          onExecute={this._onPrimaryButtonClicked}
        /> : null}
      {this.state.buttonInfo.buttonType & TranscriptionAlertButtonType.SECONDARY_BUTTON ?
        <AppButton
          type={'AppButtonProps'}
          id="TranscriptionSecondaryButton"
          styles={getSecondaryButtonOverrideStyles()}
          text={this.state.buttonInfo.secondaryButtonText == undefined ? TranscriptionStrings.CancelButtonName : this.state.buttonInfo.secondaryButtonText}
          onExecute={this._onSecondaryButtonClicked}
        /> : null}
    </DialogFooter>);
  }

  private _onPrimaryButtonClicked = () => {
    transcriptionAlertManager.onButtonClicked(TranscriptionAlertButtonType.PRIMARY_BUTTON);
  }

  private _onSecondaryButtonClicked = () => {
    transcriptionAlertManager.onButtonClicked(TranscriptionAlertButtonType.SECONDARY_BUTTON);
  }
}

class TranscriptionAlertManager extends EventEmitter {

  private state: TranscriptionAlertState = {
    showTranscriptionAlert: false,
    information: EMPTY_STRING,
    title: EMPTY_STRING,
    buttonInfo: { buttonType: TranscriptionAlertButtonType.PRIMARY_BUTTON },
  };

  private transcriptionEventQueue: Array<TranscriptionEvent> = new Array<TranscriptionEvent>();

  constructor() {
    super();
    dispatcher.register(this.handleUpdate);
  }

  public getState = (): TranscriptionAlertState => {
    return Object.assign({}, this.state);
  }

  public handleUpdate = (action: TranscriptionEvent): void => {
    switch (action.type) {
      case TranscriptionAlertActions.ShowTranscriptionAlert:

        // If Information pop up is already showing, push event to queue, which will be taken up later.
        if (this.state.showTranscriptionAlert) {
          this.transcriptionEventQueue.push(action);
          return;
        }

        const transcriptionTranscriptionAlertState: TranscriptionAlertState = ((action).payload);
        this.state.information = transcriptionTranscriptionAlertState.information;;
        this.state.title = transcriptionTranscriptionAlertState.title;
        this.state.showTranscriptionAlert = true;
        this.state.buttonInfo = transcriptionTranscriptionAlertState.buttonInfo;
        this.emit(ChangeEvent.Change);
        break;
      case TranscriptionAlertActions.RemoveTranscriptionAlert:
        this.state.showTranscriptionAlert = false;
        this.emit(ChangeEvent.Change);
        break;
    }
  }

  public onButtonClicked = (type: TranscriptionAlertButtonType) => {
    this.state.showTranscriptionAlert = false;
    this.emit(ChangeEvent.Change);

    this.state.buttonInfo.buttonClickListener?.onButtonClicked(type);
    if (this.transcriptionEventQueue.length > 0) {
      const action: any = this.transcriptionEventQueue.pop();
      if (action != undefined)
        this.handleUpdate(action);
    }
  }
}

const transcriptionAlertManager = new TranscriptionAlertManager();
export default transcriptionAlertManager;